import { FlexColumn, FlexRow } from '@gorila-shared-ui/components';
import { Modal, ModalBody, ModalHeader } from 'baseui/modal';
import { ParagraphSmall } from 'baseui/typography';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';
import { useFeedback } from '../../../../hooks/useFeedback';
import { useStyles } from '../../../../hooks/useStyles';
import { updateFleetDriverSubClients } from '../../../../services/fleetService';
import { apiParamsState } from '../../../../storage/app';
import { FleetDriver } from '../../../../types/drivers';
import { getDriverFullName } from '../../../../utils/drivers';
import ConfirmModal from '../../../shared/ConfirmModal';
import FleetDriverForm from './FleetDriverForm';
import FleetDriverMatchingCard from './FleetDriverMatchingCard';

type Props = Readonly<{
  isOpen: boolean;
  driver?: FleetDriver;
  onClose: (update?: boolean) => void;
}>;

function FleetDriverModal({ onClose, isOpen, driver }: Props) {
  const { css } = useStyles();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const apiParams = useRecoilValue(apiParamsState);
  const [matchingDriver, setMatchingDriver] = useState<FleetDriver & { newPhoto: string }>();

  const afterSave = () => {
    onClose(true);
  };

  const onCancel = () => {
    onClose(true);
  };

  const closeMatchingDriver = () => {
    setMatchingDriver(undefined);
    onClose(false);
  };

  const confirmMatchingDriver = async () => {
    if (!matchingDriver?.photo || !apiParams) return;
    const { id, error } = await updateFleetDriverSubClients({
      clientId: apiParams.clientId,
      subClientId: apiParams.subClientId,
      driverId: matchingDriver._id,
      photo: matchingDriver.newPhoto,
    });
    if (id && !error) {
      showPositiveFeedback('Se agregó a tu lista de conductores exitosamente.');
      afterSave();
    } else {
      onCancel();
      showFailFeedback(error || 'Ocurrió un error, inténtalo más tarde.');
    }
    setMatchingDriver(undefined);
  };

  return (
    <>
      <ConfirmModal
        isOpen={!!matchingDriver}
        onCancel={closeMatchingDriver}
        onConfirm={confirmMatchingDriver}
        title="Se encontró una coincidencia"
        description={
          <FlexColumn>
            {!!matchingDriver && <FleetDriverMatchingCard driver={matchingDriver} />}
            <ParagraphSmall margin={0}>Da click en "Aceptar" para agregarlo a tu lista de conductores</ParagraphSmall>
          </FlexColumn>
        }
      />
      {createPortal(
        <Modal
          animate
          closeable
          onClose={() => onClose()}
          isOpen={isOpen && !matchingDriver}
          size="auto"
        >
          <FlexRow
            gap={0}
            classNames={css({
              justifyContent: 'space-between',
              margin: '6px 2rem 0px 0px',
            })}
          >
            <ModalHeader
              className={css({
                marginBottom: '0 !important',
              })}
            >
              {driver ? getDriverFullName(driver) : 'Nuevo conductor'}
            </ModalHeader>
          </FlexRow>
          <ModalBody>
            <FlexColumn
              classNames={css({
                width: '70vw',
                maxWidth: '600px',
                minWidth: '350px',
                boxSizing: 'border-box',
                padding: '1rem',
                borderTop: '1px solid rgb(226, 226, 226)',
                maxHeight: '80vh',
              })}
            >
              <FleetDriverForm
                driver={driver}
                onCancel={onCancel}
                afterSave={afterSave}
                onMatch={setMatchingDriver}
              />
            </FlexColumn>
          </ModalBody>
        </Modal>,
        document.body,
      )}
    </>
  );
}

export default FleetDriverModal;
